import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiMail, FiMessageCircle } from 'react-icons/fi';
import { getSuperAdminDerasars, updateDerasarNotificationChannels } from '../../services/api';
import { getApiErrorMessage } from '../../services/apiError';
import styles from './NotificationChannels.module.css';

const Switch = ({ checked, onChange }) => (
  <label className={styles.switch}>
    <input type="checkbox" checked={!!checked} onChange={onChange} />
    <span className={styles.slider} />
  </label>
);

const NotificationChannels = () => {
  const [derasars, setDerasars] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSuperAdminDerasars({});
      setDerasars((res.data || []).map((d) => ({
        id: d.id,
        name: d.name,
        city: d.city,
        status: d.status,
        notifyEmailEnabled: !!d.notifyEmailEnabled,
        notifyWhatsappEnabled: !!d.notifyWhatsappEnabled,
      })));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load derasars'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, key) => {
    const prev = derasars;
    const nextVal = !derasars.find((d) => d.id === id)?.[key];
    setDerasars((list) => list.map((d) => (d.id === id ? { ...d, [key]: nextVal } : d)));
    try {
      await updateDerasarNotificationChannels(id, { [key]: nextVal });
    } catch (err) {
      setDerasars(prev);
      toast.error(getApiErrorMessage(err, 'Failed to update channel'));
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Notification Settings</h2>
        <p className={styles.subtitle}>
          Enable or disable Email and WhatsApp delivery for each derasar. When a channel
          is off, that derasar&apos;s admins and members can&apos;t use it — in-app
          notifications always work.
        </p>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Derasar</th>
                <th className={styles.center}><FiMail /> Email</th>
                <th className={styles.center}><FiMessageCircle /> WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className={styles.muted}>Loading…</td></tr>
              ) : derasars.length === 0 ? (
                <tr><td colSpan={3} className={styles.muted}>No derasars found.</td></tr>
              ) : derasars.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className={styles.name}>{d.name}</div>
                    {d.city && <div className={styles.sub}>{d.city}</div>}
                  </td>
                  <td className={styles.center}>
                    <Switch checked={d.notifyEmailEnabled} onChange={() => toggle(d.id, 'notifyEmailEnabled')} />
                  </td>
                  <td className={styles.center}>
                    <Switch checked={d.notifyWhatsappEnabled} onChange={() => toggle(d.id, 'notifyWhatsappEnabled')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationChannels;
